import {Component, ViewChild} from '@angular/core';
import {RouterOutlet} from '@angular/router';
import {FooterComponent} from '../components/footer/footer.component';
import {HeaderComponent} from '../components/header/header.component';
import {IBaseComponent} from '../../../sdk/src/components/ibase/ibase.component';
import {MegaMenuItem, MenuItem} from 'primeng/api';
import {CpmCCustomerUserMembershipApiService} from '../../../gen/cpm-c-api-ts/src/services/c-common/cpmc-customer-user-membership.api-service';
import {CpmCUserRoleApiService} from '../../../gen/cpm-c-api-ts/src/services/c-common/cpmc-user-role.api-service';
import {ECpmCRouteTypeApiEnum} from '../../../gen/cpm-c-api-ts/src/enums/cpmc-sdk.api-enums';
import {CpmCUserApiService} from '../../../gen/cpm-c-api-ts/src/services/c-global/cpmc-user.api-service';
import {CpmCOO8ConnectorApiService} from '../../../gen/cpm-c-api-ts/src/services/c-global/cpmc-oo8-connector.api-service';
import {CpmCS3BucketApiService} from '../../../gen/cpm-c-api-ts/src/services/c-global/cpmc-s3-bucket.api-service';
import {CpmCJobsInstanceApiService} from '../../../gen/cpm-c-api-ts/src/services/c-global/cpmc-jobs-instance.api-service';

/**
 * Global layout component
 */
@Component({
    selector: 'app-global-layout',
    imports: [RouterOutlet, FooterComponent, HeaderComponent],
    templateUrl: './global-layout.component.html',
    styleUrls: ['./global-layout.component.scss']
})
export class GlobalLayoutComponent extends IBaseComponent {
    @ViewChild(HeaderComponent, {static: true})
    protected header?: HeaderComponent;

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    protected override async onReady(): Promise<void> {
        this.header?.setMenuItems(this.getMenuItems());
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * Init menu items
     */
    private getMenuItems(): MegaMenuItem[] {
        const menuItems: MegaMenuItem[] = [
            {
                label: 'Customers',
                routerLink: ['/'],
                routerLinkActiveOptions: {exact: true}
            }
        ];

        const administrationMegaMenu = this.getAdministrationMegaMenuItem();
        if (administrationMegaMenu?.items?.length && administrationMegaMenu?.items?.length > 0)
            menuItems.push(administrationMegaMenu);

        return menuItems;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Build administration mega menu item
     * @returns Administration mega menu item
     */
    private getAdministrationMegaMenuItem(): MegaMenuItem {
        const result: MegaMenuItem = {
            label: 'Administration',
            items: [],
            appLogicalRouterLink: '/administration'
        };

        const colA: MenuItem[] = [];
        const colB: MenuItem[] = [];

        // ===============================================================

        const jobsGroup: MenuItem = {
            label: 'Jobs',
            items: []
        };
        if (
            this.globalsService.sessionService.isAuthorizedForRouteResource(
                CpmCJobsInstanceApiService.endpointList(),
                ECpmCRouteTypeApiEnum.READ
            )
        ) {
            jobsGroup.items?.push({
                label: 'Action',
                routerLink: ['/administration/jobs-action']
            });
        }

        if (jobsGroup.items?.length && jobsGroup.items?.length > 0) colA.push(jobsGroup);

        // ===============================================================

        const connectorsGroup: MenuItem = {
            label: 'Connectors',
            items: []
        };
        if (
            this.globalsService.sessionService.isAuthorizedForRouteResource(
                CpmCOO8ConnectorApiService.endpointList(),
                ECpmCRouteTypeApiEnum.READ
            )
        ) {
            connectorsGroup.items?.push({
                label: 'OO8 Connectors',
                routerLink: ['/administration/oo8-connectors']
            });
        }

        if (
            this.globalsService.sessionService.isAuthorizedForRouteResource(
                CpmCS3BucketApiService.endpointNew(),
                ECpmCRouteTypeApiEnum.CREATE
            )
        ) {
            connectorsGroup.items?.push({
                label: 'S3 Buckets',
                routerLink: ['/administration/s3-buckets']
            });
        }

        if (connectorsGroup.items?.length && connectorsGroup.items?.length > 0) colA.push(connectorsGroup);

        // ===============================================================

        const permissionsGroup: MenuItem = {
            label: 'Permissions',
            items: []
        };
        if (
            this.globalsService.sessionService.isAuthorizedForRouteResource(
                CpmCUserApiService.endpointList(),
                ECpmCRouteTypeApiEnum.READ
            )
        ) {
            permissionsGroup.items?.push({
                label: 'Users',
                routerLink: ['/administration/users']
            });
        }
        if (
            this.globalsService.sessionService.isAuthorizedForRouteResource(
                CpmCUserRoleApiService.endpointList(undefined),
                ECpmCRouteTypeApiEnum.READ
            )
        ) {
            permissionsGroup.items?.push({
                label: 'User Roles',
                routerLink: ['/administration/user-roles']
            });
        }
        if (
            this.globalsService.sessionService.isAuthorizedForRouteResource(
                CpmCCustomerUserMembershipApiService.endpointList(undefined),
                ECpmCRouteTypeApiEnum.READ
            )
        ) {
            permissionsGroup.items?.push({
                label: 'Customer User Memberships',
                routerLink: ['/administration/customer-user-memberships']
            });
        }

        if (permissionsGroup.items?.length && permissionsGroup.items?.length > 0) colB.push(permissionsGroup);

        // ===============================================================

        if (colA.length > 0) result.items?.push(colA);
        if (colB.length > 0) result.items?.push(colB);

        return result;
    }
}
